import { Page, Tile } from "@Types/pagify";
import User from "@Types/user";
import { getLink } from "./";

export const addCategoryPageStructuredData = (category: Page, locale: string, user: User): void => {
  const productSection = category.sections.find(
    (section) => section.displayType === "large_product_cards_web"
  );
  if (!productSection) return;

  const products = category.tiles.filter((tile) =>
    new Set(productSection.tileSlugs).has(tile.slug)
  );

  for (const product of products) {
    const name = !!product.textBlock1 ? product.textBlock1[0].text : undefined;
    const description = product.textBlock4?.reduce((acc, curr) => `${acc} ${curr.text}`, "");
    const price = !!product.textBlock3 ? product.textBlock3[0].text : undefined;
    const tag = !!product.metadata?.products ? product.metadata.products[0].tag : undefined;
    const image = product.images[0].desktop["1x"];
    const url = `${window.location.origin}${getLink(product.navigationLink, locale) || ""}`;
    const ratingValue = product.metadata?.ratingValue;
    const reviewCount = product.metadata?.reviewCount || undefined;

    const productData = getProductData(
      name,
      description,
      price,
      tag,
      image,
      url,
      ratingValue,
      reviewCount,
      user
    );
    !!productData && addScriptToHead(productData, tag);
  }

  const faqSection = category.sections.find((section) => section.displayType === "faq_web");
  if (!faqSection) return;

  const faq = category.tiles.filter((tile) => new Set(faqSection.tileSlugs).has(tile.slug));
  const faqContent = getFaqData(faq);
  !!faqContent && addScriptToHead(faqContent, "faq");
};

export const addProductPageStructuredData = (
  product: Page,
  imageTile: Tile,
  tile: Tile,
  user: User
): void => {
  const name = product.metadata?.metaTitle;
  const description = product.metadata?.metaDescription;
  const price = tile.metadata?.web?.products
    ? tile.metadata.web.products[0].productPrice
    : undefined;
  const tag = tile.metadata?.web?.products ? tile.metadata.web.products[0].productTag : undefined;
  const image = imageTile.images[0].desktop["1x"];
  const url = window.location.href;
  const ratingValue = tile.metadata?.ratingValue;
  const reviewCount = tile.metadata?.reviewCount || undefined;

  const productData = getProductData(
    name,
    description,
    price,
    tag,
    image,
    url,
    ratingValue,
    reviewCount,
    user
  );
  !!productData && addScriptToHead(productData, tag);
};

export const getFaqData = (tiles: Tile[]): string | undefined => {
  const faqItems = tiles.map((tile) => {
    if (!!tile.textBlock1 && !!tile.textBlock2) {
      return {
        "@type": "Question",
        acceptedAnswer: {
          "@type": "Answer",
          text: tile.textBlock2.reduce((acc, curr) => `${acc} ${curr.text}`, ""),
        },
        name: tile.textBlock1[0].text,
      };
    }
  });

  if (!!faqItems) {
    const scriptContent = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqItems,
    };
    return JSON.stringify(scriptContent);
  }
};

export const getProductData = (
  name: string | undefined,
  description: string | undefined,
  price: string | undefined,
  tag: string | undefined,
  image: string | undefined,
  url: string | undefined,
  ratingValue: string | undefined,
  reviewCount: number | undefined,
  user: User
): string | undefined => {
  if (!name || !description || !price || !tag || !image || !url) {
    return;
  }
  const scriptContent = {
    "@context": "http://schema.org/",
    "@type": "Product",
    ...(!!ratingValue &&
      !!reviewCount && {
        aggregateRating: {
          "@type": "AggregateRating",
          bestRating: "10",
          ratingValue: ratingValue,
          reviewCount: reviewCount,
          worstRating: "1",
        },
      }),
    brand: { "@type": "Brand", name: "Cheerz" },
    description: description,
    image: image,
    name: name,
    offers: {
      "@type": "Offer",
      availability: "http://schema.org/InStock",
      price: parseFloat(price.replace(",", ".").replace(/\$|€|£,/g, "")),
      priceCurrency: user.currency,
      priceValidUntil: new Date(new Date().getFullYear() + 1, 11, 31),
      url: url,
    },
    productID: tag,
    sku: tag,
  };
  return JSON.stringify(scriptContent);
};

export const addScriptToHead = (content: string, id: string | undefined): void => {
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.id = id || "";
  script.textContent = content;
  document.head.appendChild(script);
};
